import api from './api';

export interface KundliProfile {
    id: string;
    user_id: string;
    profile_name: string;
    name: string;
    dob: string;
    birth_time: string;
    birth_location: string;
    location?: string; // Fallback for any legacy data
    latitude: number;
    longitude: number;
    timezone: string;
    gender: string;
    marital_status?: string;
    occupation?: string;
    is_default?: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface CreateKundliData {
    profile_name?: string;
    name: string;
    dob: string;
    birth_time: string;
    birth_location: string;
    latitude: number;
    longitude: number;
    timezone?: string;
    gender: string;
    marital_status?: string;
    occupation?: string;
}

export interface UpdateKundliData {
    profile_name?: string;
    name?: string;
    dob?: string;
    birth_time?: string;
    birth_location?: string;
    latitude?: number;
    longitude?: number;
    timezone?: string;
    gender?: string;
    marital_status?: string;
    occupation?: string;
}

export interface DailyHoroscopeData {
    date: string;
    formatted_date?: string;
    profile_name?: string;
    name?: string;
    sign: string;
    sun_sign: string;
    moon_sign: string;
    summary: string;
    prediction?: {
        personal?: string;
        health?: string;
        profession?: string;
        emotions?: string;
        travel?: string;
        luck?: string;
    };
    guidance: {
        do: string[];
        avoid: string[];
        remedy: string[];
        luck_message: string;
    };
    lucky_elements?: {
        color: string;
        color_code: string;
        number: number;
        time: string;
        direction?: string;
        rahu_kalam?: string;
    };
    scores: {
        health: number;
        wealth: number;
        career: number;
        relationship: number;
        luck: number;
        travel?: number;
    };
    mood: string;
    energy: string;
    planetary_influences: string[];
}

export type AstroAngle = 'MC' | 'IC' | 'ASC' | 'DSC';

/** A polyline of [longitude, latitude] pairs, already split at the antimeridian. */
export type AstroSegment = [number, number][];

export interface AstroPlanetLines {
    declination: number;
    right_ascension: number;
    MC: AstroSegment[];
    IC: AstroSegment[];
    ASC: AstroSegment[];
    DSC: AstroSegment[];
}

export interface AstrocartographyMap {
    profile: {
        id: string;
        name: string;
        birth_location: string;
        latitude: number;
        longitude: number;
    };
    planets: Record<string, { sign: string; longitude: number; declination: number }>;
    lines: Record<string, AstroPlanetLines>;
}

export const kundliService = {
    // Get all kundli profiles for the current user
    getProfiles: async (): Promise<{ kundli_profiles: KundliProfile[]; count: number }> => {
        const response = await api.get('/api/kundli/');
        return response.data;
    },

    // Planetary lines for the astrocartography map
    getAstrocartographyMap: async (profileId: string): Promise<AstrocartographyMap> => {
        const response = await api.get(`/api/kundli/astrocartography-map/${profileId}`);
        return response.data.data;
    },

    // Get a specific kundli profile by ID
    getProfile: async (profileId: string): Promise<{ kundli: KundliProfile }> => {
        const response = await api.get(`/api/kundli/${profileId}`);
        return response.data;
    },

    // Create a new kundli profile
    createProfile: async (data: CreateKundliData): Promise<{ message: string; kundli: KundliProfile }> => {
        const response = await api.post('/api/kundli/', data);
        return response.data;
    },

    // Update an existing kundli profile
    updateProfile: async (profileId: string, data: UpdateKundliData): Promise<{ message: string; kundli: KundliProfile }> => {
        const response = await api.put(`/api/kundli/${profileId}`, data);
        return response.data;
    },

    // Delete a kundli profile
    deleteProfile: async (profileId: string): Promise<{ message: string }> => {
        const response = await api.delete(`/api/kundli/${profileId}`);
        return response.data;
    },

    // Set a kundli as default
    setDefault: async (kundliId: string): Promise<{ success: boolean; message: string }> => {
        const response = await api.post('/api/kundli/set-default', { kundli_id: kundliId });
        return response.data;
    },

    // Get the user's default kundli
    getDefault: async (): Promise<{ kundli: KundliProfile }> => {
        const response = await api.get('/api/kundli/default');
        return response.data;
    },

    // Get daily horoscope
    getDailyHoroscope: async (dateParam: 'today' | 'yesterday' | 'tomorrow' = 'today', lang?: string, refresh: boolean = false): Promise<DailyHoroscopeData> => {
        const response = await api.get('/api/kundli/daily-horoscope', {
            params: {
                date: dateParam,
                lang: lang,
                refresh: refresh
            },
            // @ts-expect-error custom axios config key handled in response interceptor
            suppress404: true
        });
        return response.data;
    },

    // Generate raw kundli data (public)
    generateRawData: async (data: any): Promise<any> => {
        const response = await api.post('/api/kundli/generate-raw-data', data);
        return response.data;
    },

    // Generate compatibility analysis (public)
    generateCompatibilityAnalysis: async (person1: any, person2: any, language: 'english' | 'hindi' = 'english'): Promise<any> => {
        const response = await api.post('/api/kundli/compatibility-analysis', {
            person1,
            person2,
            language
        });
        return response.data;
    },
};
